import { useDisclosure } from "@mantine/hooks";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { showErrorToast } from "#app/notifications";
import { colors } from "#app/theme/v1/colors";
import { fontSizes } from "#app/theme/v1/fontSizes";
import { radius } from "#app/theme/v1/radius";
import { spacing } from "#app/theme/v1/spacing";
import { Company } from "#modules/company/infra/Company.dto";
import { CompanyInfoDetails } from "#modules/company/view/CompanyInfoDetails";
import { EditCompanyInfosDetails } from "#modules/company/view/EditCompanyInfoDetails";
import { UserTableContent } from "#modules/company/view/UserTableContent";
import { useUpdateCompany } from "#modules/company/view/useUpdateCompany";
import { User } from "#modules/users/infra/User.dto";
import { Icon } from "#shared/icons/Icon";
import { uploadFile } from "#shared/infra/uploadFile.service";
import { Box } from "#shared/view/components/Box";
import { CopyButton } from "#shared/view/components/CopyButton";
import { Group } from "#shared/view/components/Group";
import { Image } from "#shared/view/components/Image";
import { Space } from "#shared/view/components/Space";
import { Text } from "#shared/view/components/Text";
import { Title } from "#shared/view/components/Title";
import { BasicTable } from "#shared/view/designSystem/BasicTable";
import { Button } from "#shared/view/designSystem/Button";
import { Doc } from "#shared/view/designSystem/Doc";
import { FileButton } from "#shared/view/designSystem/FileButton";
import { FileTypeSchema, formatFileSize, getAccept, UPLOAD_CONFIG } from "shared";

import { useFileInformations } from "./useFileInformations";

type Props = { company: Company; team: User[] };

export function CompanyInfos({ company, team }: Props) {
  const { t } = useTranslation();
  const { updateCompany } = useUpdateCompany();
  const [isEditingCompany, { close: disableEditing, open: enableEditing }] = useDisclosure(false);

  const { fileInfo, fetchFileInfo } = useFileInformations(company.cgvUrl || "");

  useEffect(() => {
    if (company.cgvUrl) {
      fetchFileInfo();
    }
  }, [company.cgvUrl, fetchFileInfo]);

  const uploadCGV = async (file: File | null) => {
    if (!file) {
      return;
    }
    if (file.size > UPLOAD_CONFIG.COMPANY_CGV.maxFileSize) {
      showErrorToast({
        title: t("common.fileTooLarge.title"),
        message: t("common.fileTooLarge.mustBeLessThan", {
          maxSize: formatFileSize(UPLOAD_CONFIG.COMPANY_CGV.maxFileSize),
        }),
      });
      return;
    }
    const { fileUrl: url } = await uploadFile({
      file,
      type: FileTypeSchema.enum.COMPANY_CGV,
      isUserConnected: true,
    });
    updateCompany({ cgvUrl: url });
  };

  const uploadLogo = async (file: File | null) => {
    if (!file) {
      return;
    }
    if (file.size > UPLOAD_CONFIG.COMPANY_LOGO.maxFileSize) {
      showErrorToast({
        title: t("common.fileTooLarge.title"),
        message: t("common.fileTooLarge.mustBeLessThan", {
          maxSize: formatFileSize(UPLOAD_CONFIG.COMPANY_LOGO.maxFileSize),
        }),
      });
      return;
    }
    const { fileUrl: url } = await uploadFile({
      file,
      type: FileTypeSchema.enum.COMPANY_LOGO,
      isUserConnected: true,
    });
    updateCompany({ logoUrl: url });
  };

  return (
    <>
      <Space h="lg" />
      <div>
        <Title order={5}>Coordonnées</Title>
        <Group mt="sm" style={styles.logoWrapper}>
          <Box style={styles.logoContainer}>
            {!!company.logoUrl && <Image fit="contain" src={company.logoUrl} />}
          </Box>
          <Group
            style={{
              position: "relative",
              display: "inline-block",
            }}
          >
            <FileButton
              accept={getAccept(UPLOAD_CONFIG.COMPANY_LOGO.extensions)
                ?.map((ext) => `.${ext}`)
                .join(",")}
              onChange={uploadLogo}
            >
              {(props) => (
                <Button variant="filled" icon={<Icon name="upload" />} {...props}>
                  Importer un logo
                </Button>
              )}
            </FileButton>
          </Group>
        </Group>
      </div>
      {isEditingCompany ? (
        <EditCompanyInfosDetails company={company} onSubmit={disableEditing} />
      ) : (
        <CompanyInfoDetails company={company} onEdit={enableEditing} />
      )}
      <Space h="lg" />
      <div>
        <Title order={5}>CGV Entreprise</Title>
        <Box my="sm">
          {company.cgvUrl && fileInfo && (
            <>
              <Doc
                name={fileInfo.name}
                description={fileInfo.size && `${(fileInfo.size / 1000).toFixed(0)}KB`}
                url={company.cgvUrl}
              />
            </>
          )}
        </Box>
        <FileButton accept="application/pdf" onChange={uploadCGV}>
          {(props) => (
            <Button variant="filled" icon={<Icon name="upload" />} {...props}>
              {company.cgvUrl ? "Remplacer les CGV" : "Télécharger les CGV"}
            </Button>
          )}
        </FileButton>
      </div>
      <Space h="lg" />
      <div>
        <Title order={5}>Référents</Title>
        <Space h="xss" />
        <Text fz={fontSizes.medium}>
          Retrouvez la liste des référents internes à votre entreprise.
        </Text>
        <Space h="xs" />
        <BasicTable
          headers={[
            { id: "photo", label: "Photo" },
            { id: "fullName", label: "Nom Complet" },
            { id: "function", label: "Fonction" },
            { id: "email", label: "Email" },
          ]}
          elements={team.map((user, userIndex) => UserTableContent({ user, userIndex }))}
        />
      </div>
      <div>
        <Space h="lg" />
        <div>
          <b>Identifiant unique : </b> {company.id}{" "}
          <CopyButton value={company.id}>
            {({ copied, copy }) => (
              <Button color={copied ? "green" : ""} onClick={copy}>
                {copied ? "Identifiant copié" : "Copier l'identifiant"}
              </Button>
            )}
          </CopyButton>
          <br />
          <p>
            Cet identifiant confidentiel sera demandé aux utilisateurs qui souhaitent rejoindre
            votre entreprise sur Fabera.
          </p>
        </div>
      </div>
    </>
  );
}

const styles = {
  logoWrapper: {
    gap: 0,
  },
  logoContainer: {
    border: `1px solid ${colors.gray[3]}`,
    borderRadius: radius.sm,
    width: 145,
    height: 126,
    marginRight: spacing.lg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
};
